import React, { useEffect } from "react";
import { resolveActiveEmployee } from "../Shared/ScreenUtils.jsx";

import { AdminChangePasswordScreen } from "./AdminChangePasswordScreen.jsx";
import { AxonApiScreen } from "./access/AxonApiScreen.jsx";
import { ProposalsScreen } from "../Products/axon/index.js";
import { VibeTestingScreen } from "../Products/engineering/VibeTestingScreen.jsx";
import { BankDetailsScreen } from "../Products/payroll/BankDetailsScreen.jsx";
import { DeactivateEmployeeScreen } from "../Products/people/DeactivateEmployeeScreen.jsx";
import { EmployeeRegisterScreen } from "../Products/payroll/EmployeeRegisterScreen.jsx";
import { DelayManagementScreen } from "../Products/projects/DelayManagementScreen.jsx";
import GrowthRoadmapScreen from "../Products/people/GrowthRoadmapScreen.jsx";
import { DocsPortalScreen } from "../Products/documents/DocsPortalScreen.jsx";
import { CompanyPolicyScreen } from "../Products/documents/CompanyPolicyScreen.jsx";

import { FinanceScreen } from "../Products/payroll/FinanceScreen.jsx";
import { PnLScreen } from "../Products/payroll/PnLScreen.jsx";
import { HomeScreen } from "./HomeScreen.jsx";
import { HrmsScreen } from "../Products/people/HrmsScreen.jsx";
import { LeaveApplyScreen } from "../Products/timeoff/LeaveApplyScreen.jsx";
import { LeaveRequestsScreen } from "../Products/timeoff/LeaveRequestsScreen.jsx";
import { LmsScreen } from "../Products/crm/LmsScreen.jsx";
import { SalesPipelineScreen } from "../Products/crm/SalesPipelineScreen.jsx";
import { SalesPerformanceScreen } from "../Products/crm/SalesPerformanceScreen.jsx";
import { IntegrationsScreen } from "./integrations/IntegrationsScreen.jsx";
import { SalesCrmBoardScreen } from "../Products/crm/SalesCrmBoardScreen.jsx";
import { NotificationsScreen } from "./NotificationsScreen.jsx";
import OnboardingScreen from "./OnboardingScreen.jsx";
import JuniorOnboardingScreen from "../Products/learning/JuniorOnboardingScreen.jsx";
import SeniorOnboardingScreen from "../Products/learning/SeniorOnboardingScreen.jsx";
import PlaybookScreen from "../Products/crm/addons/playbook/PlaybookScreen.jsx";
import RevOpsScreen from "../Products/revops/RevOpsScreen.jsx";
import RevOpsV2Screen from "../Products/revops/RevOpsV2Screen.jsx";
import { PayslipsScreen } from "../Products/payroll/PayslipsScreen.jsx";
import { ProjectsScreen } from "../Products/projects/ProjectsScreen.jsx";
import { ProjectManagementScreen } from "../Products/projects/ProjectManagementScreen.jsx";
import { canAccessRoute as pageAccessCanAccessRoute, pageForRoute, switchedOffProduct } from "../Services/pageAccess.js";
import { NotInPlanScreen } from "./NotInPlanScreen.jsx";
import { OperatorConsoleScreen } from "./operator/OperatorConsoleScreen.jsx";
import { companyBrandName } from "../Shared/companyBrand.js";
import { AuditScreen } from "./audit/AuditScreen.jsx";
import { SendCertificateScreen } from "../Products/hiring/SendCertificateScreen.jsx";
import { DevOpsScreen } from "../Products/devops/DevOpsScreen.jsx";
import { CultureCheckScreen } from "../Products/hiring/CultureCheckScreen.jsx";
import { JoiningReviewScreen } from "../Products/hiring/JoiningReviewScreen.jsx";

// Route access asks the page-access grid the server sent: the same question the server asks
// itself when the page's endpoints are called. The pages registry is the one source of truth.
function canAccessRoute(path, me) {
  return pageAccessCanAccessRoute(me, path);
}

function Redirect({ to, navigate }) {
  useEffect(() => {
    navigate(to);
  }, [navigate, to]);
  return null;
}

// Window titles, set centrally so every screen is identifiable from the tab /
// window switcher. Title-tag convention: the most specific thing first, its
// section after, "|" separated, short enough to survive tab truncation.
// Detail screens overwrite this with record-specific titles once their data
// loads (e.g. "Hanish Raghunathan - Acme Corp | Sales CRM" from LmsScreen).
const DEFAULT_TITLE = "Intranet";
function routeTitle(path, brand = DEFAULT_TITLE) {
  if (path.startsWith("/lms/analytics")) return "Sales Pipeline | Sales CRM";
  if (path.startsWith("/sales-performance")) return "Sales Performance | Sales CRM";
  if (/^\/lms\/[^/]+\/?$/.test(path)) return "Lead | Sales CRM"; // placeholder until the lead loads
  if (path.startsWith("/lms")) return "Lead Overview | Sales CRM";
  if (path.startsWith("/crm")) return "Pipeline Board | Sales CRM";
  if (path.startsWith("/hrms")) return "HRMS | Intranet";
  if (path.startsWith("/leave-requests")) return "Leave Requests | Intranet";
  if (path.startsWith("/leave")) return "Apply Leave | Intranet";
  if (path.startsWith("/delays")) return "Delay Management | Intranet";
  if (path.startsWith("/projects/management") || path.startsWith("/projects/sanity")) return "Project Management | Intranet";
  if (path.startsWith("/projects")) return "Projects | Intranet";
  if (path.startsWith("/docs")) return "Documents | Intranet";
  if (path.startsWith("/policies")) return "Company Policies | Intranet";
  if (path.startsWith("/vibe-testing")) return "Vibe testing | Intranet";
  if (path.startsWith("/growth-roadmap")) return "Growth Roadmap | Intranet";
  if (path.startsWith("/Bankdetails") || path.startsWith("/bank")) return "Bank Details | Intranet";
  if (path.startsWith("/joining-reviews")) return "IDs waiting | Intranet";
  if (path.startsWith("/onboarding/culture")) return `Before you accept | ${brand}`;
  if (path.startsWith("/Onboard/Send_Offer")) return "Send Offer | Intranet";
  if (path.startsWith("/send-certificate")) return "Send Certificate | Intranet";
  if (path.startsWith("/deactivate")) return "Deactivate Employee | Intranet";
  if (path.startsWith("/employee-register") || path.startsWith("/employee-registrar")) return "Employee Register | Intranet";
  if (path.startsWith("/payslips")) return "Payslips | Intranet";
  if (path.startsWith("/pnl")) return "P&L | Intranet";
  if (path.startsWith("/devops")) return "DevOps | Intranet";
  if (path.startsWith("/payments") || path.startsWith("/finance")) return "Finance | Intranet";
  if (path.startsWith("/notifications")) return "Notifications | Intranet";
  if (path.startsWith("/integrations")) return "Integrations | Intranet";
  if (path.startsWith("/axon-api")) return "AXON API | Intranet";
  if (path.startsWith("/proposals")) return "Suggested Tasks | Intranet";
  if (path.startsWith("/platform")) return "Companies | Intranet";
  if (path.startsWith("/audit")) return "Audit | Intranet";
  if (path.startsWith("/change-password") || path.startsWith("/admin/change-password")) return "Change Password | Intranet";
  if (path.startsWith("/sales-onboarding")) return "AE Onboarding | Intranet";
  if (path.startsWith("/junior-onboarding")) return "SDR Onboarding | Intranet";
  if (path.startsWith("/revops-v2")) return "RevOps V2 | Intranet";
  if (path.startsWith("/revops")) return "RevOps | Intranet";
  if (path.startsWith("/onboarding")) return "Onboarding | Intranet";
  if (path.startsWith("/profile")) return "My Profile | Intranet";
  if (path.startsWith("/home")) return "Home | Intranet";
  return DEFAULT_TITLE;
}

export function RouteRenderer(props) {
  const path = (props.route || "").split("?")[0];
  const { data, navigate, loading } = props;
  const me = data?.me;
  const ready = !loading && Boolean(me);

  // Hooks always run, in the same order, on every render. They used to sit after an early
  // `return null`, which makes React throw "Rendered more hooks than during the previous
  // render" the moment loading finishes (triage G16, FE-03-01).
  useEffect(() => {
    if (ready) document.title = routeTitle(path, companyBrandName(me?.company));
  }, [path, ready, me?.company]);

  useEffect(() => {
    if (!ready) return;
    if (me?.auditorOnly && !path.startsWith("/audit")) {
      navigate("/audit/");  // an auditor-only account lands on the audit portal
    } else if (!canAccessRoute(path, me)) {
      navigate("/home/");
    }
  }, [path, me, navigate, ready]);

  if (!ready) return null;

  if (!canAccessRoute(path, me)) {
    return null;
  }

  // A product the company has switched off: one shared page that says so, names the
  // product and who can add it (plugin platform). Never shown while every product is on.
  const offProduct = switchedOffProduct(me, pageForRoute(path));
  if (offProduct) {
    return <NotInPlanScreen productCode={offProduct} navigate={navigate} onPluginsChanged={() => props.reload && props.reload(["me"])} />;
  }
  // Audit portal (S9): auditors with an active engagement only; not a page grant.
  if (path.startsWith("/audit")) return <AuditScreen />;
  if (path.startsWith("/platform") && props.data?.me?.user?.isOperator) return <OperatorConsoleScreen data={props.data} reload={props.reload} />;

  if (path.startsWith("/sales-onboarding")) return <SeniorOnboardingScreen {...props} />;
  if (path.startsWith("/junior-onboarding")) return <JuniorOnboardingScreen {...props} />;
  if (path.startsWith("/playbook")) return <PlaybookScreen {...props} />;
  // /revops-v2 before /revops — startsWith("/revops") would otherwise swallow it.
  if (path.startsWith("/revops-v2")) return <RevOpsV2Screen {...props} />;
  if (path.startsWith("/revops")) return <RevOpsScreen {...props} />;
  if (path.startsWith("/onboarding")) return <OnboardingScreen {...props} />;
  if (path.startsWith("/hrms")) return <HrmsScreen {...props} />;
  if (path.startsWith("/leave-requests")) return <LeaveRequestsScreen {...props} />;
  if (path.startsWith("/leave")) return <LeaveApplyScreen {...props} />;
  if (path.startsWith("/delays")) return <DelayManagementScreen {...props} />;
  // "/projects/sanity/" is the path this page shipped under for its first week,
  // before the rename. Kept so anything already open or bookmarked still lands.
  if (path.startsWith("/projects/management") || path.startsWith("/projects/sanity")) return <ProjectManagementScreen {...props} />;
  if (path.startsWith("/projects")) return <ProjectsScreen {...props} />;
  // Deprecated / retired routes → Home.
  // Marketing Projects, Assessments, Feedback removed; Projects (Old) and
  // Project Management (Old) retired 2026-08-14.
  //
  // /mcp → Home is intentional (V2.5 Phase 2, 2026-09-23): MCP UI is Admin-only
  // (Admin Panel → "MCP agents"). Do not restore a standalone /mcp page without
  // a new product decision — see Docs/V25_MCP_UI_DECISION.md.
  if (path.startsWith("/marketing-project") || path.startsWith("/assessment") || path.startsWith("/feedback") || path.startsWith("/mcp") || path.startsWith("/project/dashboard") || path.startsWith("/project/sanity")) return <Redirect to="/home/" navigate={navigate} />;
  // The internship conduct/compliance document (id 7, the only row left in
  // Documents (Old)) moved to its own stable, self-service link — see
  // Apps.AtgDocs.migrations.0004_retire_internship_conduct_doc and
  // CompanyPolicyScreen. Its old share link keeps working, just redirected,
  // rather than left to 404 the way the already-dead pre-rename
  // "/docs/post-detail/NNN" links were.
  if (path === "/docs-old/post-detail/7" || path === "/docs-old/post-detail/7/") return <Redirect to="/policies/" navigate={navigate} />;
  // Documents (Old) was removed 2026-10-08. Anything still linking into it lands
  // on the documents everyone can read, rather than on a blank page.
  if (path.startsWith("/docs-old")) return <Redirect to="/docs/company/" navigate={navigate} />;
  if (path.startsWith("/policies")) return <CompanyPolicyScreen {...props} />;
  if (path.startsWith("/docs")) return <DocsPortalScreen {...props} />;
  if (path.startsWith("/vibe-testing")) return <VibeTestingScreen {...props} />;
  if (path.startsWith("/growth-roadmap")) return <GrowthRoadmapScreen {...props} />;
  if (path.startsWith("/Bankdetails") || path.startsWith("/bank")) return <BankDetailsScreen {...props} />;
  // Standalone review page for now — reachable by URL, not in the nav, and
  // covered by the existing public "/onboarding" prefix so it needs no capability.
  if (path.startsWith("/onboarding/culture")) return <CultureCheckScreen {...props} />;
  if (path.startsWith("/joining-reviews")) return <JoiningReviewScreen {...props} />;
  if (path.startsWith("/Onboard/Send_Offer")) return <SendOfferScreen {...props} />;
  if (path.startsWith("/send-certificate")) return <SendCertificateScreen {...props} />;
  if (path.startsWith("/deactivate")) return <DeactivateEmployeeScreen {...props} />;
  if (path.startsWith("/employee-register") || path.startsWith("/employee-registrar")) return <EmployeeRegisterScreen {...props} />;
  if (path.startsWith("/payslips")) return <PayslipsScreen {...props} />;
  if (path.startsWith("/pnl")) return <PnLScreen {...props} />;
  if (path.startsWith("/devops")) return <DevOpsScreen {...props} />;
  if (path.startsWith("/payments") || path.startsWith("/finance")) return <FinanceScreen {...props} />;
  if (path.startsWith("/notifications")) return <NotificationsScreen {...props} />;
  if (path.startsWith("/integrations")) return <IntegrationsScreen {...props} />;
  if (path.startsWith("/axon-api")) return <AxonApiScreen {...props} />;
  if (path.startsWith("/proposals")) return <ProposalsScreen {...props} />;
  if (path.startsWith("/change-password") || path.startsWith("/admin/change-password")) return <AdminChangePasswordScreen {...props} />;
  if (path.startsWith("/crm")) return <SalesCrmBoardScreen {...props} />;
  if (path.startsWith("/lms/analytics")) return <SalesPipelineScreen {...props} />;
  if (path.startsWith("/sales-performance")) return <SalesPerformanceScreen {...props} />;
  if (path.startsWith("/lms")) return <LmsScreen {...props} />;
  return <HomeScreen {...props} />;
}
